
type ScriptRegistrationLedger = {
    loaded: boolean,
    // Using a Set allows for O(1) removal and prevents duplicate registrations easily
    pendingRequests: Set<HTMLElement>,
    scriptTarget?: HTMLScriptElement | HTMLTemplateElement,
    debounce?: number,
};

export class ScriptLoader {
    private static instance?: ScriptLoader;
    #ledgers = new Map<string, ScriptRegistrationLedger>();

    private constructor() {}

    static getInstance() {
        if (!ScriptLoader.instance) {
            ScriptLoader.instance = new ScriptLoader();
        }
        return ScriptLoader.instance;
    }

    /**
     * This announces that an element wants to load a script in the future. This should happen very early on
     * as it determines how many elements want to be synchronized.
     * @param identifier synchronization name
     * @param requestor HTML element wanting to load a script later on
     */
    registerScript(identifier: string, requestor: HTMLElement) {
        const ledger = this.getOrCreateLedger(identifier);
        if (!ledger.loaded) {
            ledger.pendingRequests.add(requestor);
        }
    }

    /**
     * After the registration phase this signals the intent to load (a) script(s). Using debounce to wait for
     * all elements to finish initialization and requesting the script. If all elements have requested we
     * short-circuit and load script immediately.
     * @param identifier synchronization name
     * @param requestor HTML element that registered
     * @param target Script(s) to be loaded
     */
    requestSynchronizedScriptLoad(identifier: string, requestor: HTMLElement, target: HTMLScriptElement | HTMLTemplateElement) {
        const ledger = this.#ledgers.get(identifier);
        // Case: unknown ID or already loaded
        if (!ledger || ledger.loaded) {
            return;
        }

        // Case: Asking for synchronization without prior registration
        if (!ledger.pendingRequests.has(requestor)) {
            return
        }
        // Request resolved
        ledger.pendingRequests.delete(requestor);

        if (!ledger.scriptTarget) {
            ledger.scriptTarget = target;
        }

        // Stop any running timeout
        if (ledger?.debounce) {
            clearTimeout(ledger.debounce);
            ledger.debounce = undefined;
        }

        // SHORT-CIRCUIT: Everyone is ready.
        if (ledger.pendingRequests.size === 0) {
            this.executeLoading(ledger);
        } else {
            // FALLBACK: Not everyone is ready. Wait 2s.
            // This handles cases where an element crashed or was removed before requesting.
            ledger.debounce = window.setTimeout(() => {
                this.executeLoading(ledger);
            }, 2000);
        }
    }

    /**
     * Adds script or all scripts in a template to the document header. The scripts are recreated in order
     * to actually load inert scripts. Optionally force a defer state or remove attributes — this helps
     * when loading inert scripts with irregular content type after consent.
     * @param target
     * @param forceDefer
     * @param removeAttrs
     */
    loadScript(target: HTMLScriptElement | HTMLTemplateElement, forceDefer: boolean = false, removeAttrs: string[] = []) {
        const scripts: HTMLScriptElement[] = target instanceof HTMLScriptElement
            ? [target]
            : Array.from(target.content.querySelectorAll('script'));

        scripts.forEach((script) => {
            const newScript = document.createElement('script');

            Array
                .from(script.attributes)
                .filter((attribute) => !removeAttrs.includes(attribute.name))
                .forEach((attr) => newScript.setAttribute(attr.name, attr.value));
            newScript.textContent = script.textContent;

            if (forceDefer) {
                newScript.defer = true;
            }
            document.head.appendChild(newScript);
        });
    }

    /**
     * Wrapper triggering loading of script and keeping ledger up to date.
     * @param ledger
     * @private
     */
    private executeLoading(ledger: ScriptRegistrationLedger) {
        if (ledger.loaded) return;

        ledger.loaded = true;
        ledger.pendingRequests.clear();

        if (ledger.scriptTarget) {
            this.loadScript(ledger.scriptTarget, true);
        }
    }

    private getOrCreateLedger(identifier: string) {
        if (!this.#ledgers.has(identifier)) {
            this.#ledgers.set(identifier, {
                pendingRequests: new Set<HTMLElement>(),
                loaded: false,
            });
        }
        return this.#ledgers.get(identifier)!
    }
}
